import {
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { X } from 'lucide-react';
import { PinnedTooltip } from '../chartPlugins';

/** Inputs for {@link usePinnedTooltips}. */
export interface PinnedTooltipsOptions {
  /** Ref holding the Chart.js instance, so pins can be repainted in place. */
  chartRef: MutableRefObject<any>;
  /** Value whose change drops every pin (usually the chart's datasets). */
  resetKey?: unknown;
  /** Font size of the pinned boxes. */
  fontSize?: number;
  /** Set false to turn click-to-pin off for this chart. */
  enabled?: boolean;
}

/** Pin controls handed back by {@link usePinnedTooltips}. */
export interface PinnedTooltipsApi {
  /** Tooltips currently frozen onto the chart. */
  pins: PinnedTooltip[];
  /** Chart.js `onClick` handler that toggles the pin under the cursor. */
  onChartClick: (event: any, elements: any[], chart: any) => void;
  /**
   * Options object for `plugins.pinnedTooltipsPlugin`. Its identity never
   * changes: the pins are written into it and the chart is repainted by hand,
   * so pinning never replaces the chart's options (which would throw away
   * zoom/pan state on charts that have it).
   */
  pluginOptions: { enabled: boolean; pins: PinnedTooltip[]; fontSize: number };
  /** Drop every pin. */
  clear: () => void;
}

/**
 * Click-to-pin tooltips for a Chart.js chart. Clicking a point freezes the
 * tooltip that was hovering over it onto the canvas; clicking that point again
 * removes just that one. Pins accumulate, so several points can be read side
 * by side, and they stay up until the user removes them or the chart's data
 * changes (pinned indices would otherwise point at the wrong samples).
 * @param options - See {@link PinnedTooltipsOptions}.
 * @param options.chartRef - Ref holding the Chart.js instance.
 * @param options.resetKey - Value whose change drops every pin.
 * @param options.fontSize - Font size of the pinned boxes.
 * @param options.enabled - Set false to turn click-to-pin off.
 * @returns The pins plus the click handler, plugin options and clear callback.
 */
export function usePinnedTooltips({
  chartRef,
  resetKey,
  fontSize = 12,
  enabled = true
}: PinnedTooltipsOptions): PinnedTooltipsApi {
  const [pins, setPins] = useState<PinnedTooltip[]>([]);
  const pluginOptionsRef = useRef({ enabled, pins, fontSize });
  const pluginOptions = pluginOptionsRef.current;

  // Hand the pins to the plugin and repaint, without touching the options prop.
  useEffect(() => {
    pluginOptions.enabled = enabled;
    pluginOptions.fontSize = fontSize;
    pluginOptions.pins = pins;
    chartRef.current?.update('none');
  }, [pins, enabled, fontSize, pluginOptions, chartRef]);

  useEffect(() => {
    setPins((prev) => (prev.length ? [] : prev));
  }, [resetKey]);

  const onChartClick = useCallback(
    (event: any, _elements: any[], chart: any): void => {
      if (!enabled) return;
      // Content comes from Chart.js' own tooltip model, so a pinned box is a
      // copy of the tooltip the user was looking at when they clicked.
      const tooltip = chart?.tooltip;
      if (!tooltip || !event || !chart.isPointInArea(event)) return;
      // Rebuild that model for this click first. Chart.js runs onClick before
      // the tooltip sees the click, keeps the last title/body/dataPoints even
      // once hidden, and only rebuilds them when the hovered dataset/index
      // pair changes. After the series or data behind the chart change, the
      // model can still hold the previous series' values.
      const active = chart.getElementsAtEventForMode(
        event,
        tooltip.options.mode,
        tooltip.options,
        false
      );
      if (!active.length) return;
      if (tooltip.options.reverse) active.reverse();
      tooltip.setActiveElements(active, { x: event.x, y: event.y });
      tooltip.update(true);
      const { dataPoints } = tooltip;
      if (!dataPoints?.length) return;
      /**
       * The x value behind a tooltip point, when it has one to anchor to.
       * @param p - A Chart.js tooltip data point.
       * @returns The x value as a number, or undefined.
       */
      const anchorOf = (p: any): number | undefined => {
        const datum = chart.data?.datasets?.[p.datasetIndex]?.data?.[p.dataIndex];
        const x = datum && typeof datum === 'object' ? datum.x : undefined;
        if (x instanceof Date) return x.getTime();
        return typeof x === 'number' && Number.isFinite(x) ? x : undefined;
      };
      // Identify the pin by its x value where there is one. The index-based id
      // stops matching as soon as a live chart trims a point off the front, so
      // clicking the same point again would add a second pin instead of
      // removing the first.
      const id = dataPoints
        .map((p: any) => {
          const anchor = anchorOf(p);
          return anchor === undefined
            ? `${p.datasetIndex}:${p.dataIndex}`
            : `${p.datasetIndex}@${anchor}`;
        })
        .join('|');
      /**
       * Colour of the series behind one tooltip line.
       * @param i - Index of the line within the tooltip body.
       * @returns A CSS colour.
       */
      const colorAt = (i: number): string =>
        tooltip.labelColors?.[i]?.borderColor ||
        tooltip.labelColors?.[i]?.backgroundColor ||
        '#6366F1';

      setPins((prev) => {
        if (prev.some((pin) => pin.id === id)) return prev.filter((pin) => pin.id !== id);
        const lines = (tooltip.body || []).flatMap((body: any, i: number) =>
          (body.lines || []).map((text: string) => ({ text, color: colorAt(i) }))
        );
        return [
          ...prev,
          {
            id,
            points: dataPoints.map((p: any, i: number) => ({
              datasetIndex: p.datasetIndex,
              index: p.dataIndex,
              color: colorAt(i),
              anchorX: anchorOf(p)
            })),
            title: tooltip.title || [],
            lines
          }
        ];
      });
    },
    [enabled]
  );

  const clear = useCallback(() => setPins([]), []);

  return { pins, onChartClick, pluginOptions, clear };
}

/**
 * Layer click-to-pin onto a chart's options. Any `onClick` the chart already
 * had still runs; the pin handler is added after it.
 * @param options - The chart's own options object.
 * @param api - The value returned by {@link usePinnedTooltips}.
 * @returns A new options object wired for pinning.
 */
export function withPinnedTooltips(options: any, api: PinnedTooltipsApi): any {
  const base = options || {};
  const ownOnClick = base.onClick;
  return {
    ...base,
    /**
     * Runs the chart's own click handler, then toggles the pin under the
     * cursor. Not an arrow function: Chart.js calls `onClick` with `this` set
     * to the chart and some callers rely on that.
     * @param event - The Chart.js click event.
     * @param elements - Elements under the cursor.
     * @param chart - The chart instance.
     */
    onClick(event: any, elements: any[], chart: any): void {
      if (ownOnClick) ownOnClick.call(this, event, elements, chart);
      api.onChartClick(event, elements, chart);
    },
    plugins: { ...(base.plugins || {}), pinnedTooltipsPlugin: api.pluginOptions }
  };
}

/** Props for {@link ClearPinsButton}. */
interface ClearPinsButtonProps {
  /** How many tooltips are pinned. */
  count: number;
  /** Removes them all. */
  onClear: () => void;
  /** Overrides the default pill styling. */
  className?: string;
}

const CLEAR_PINS_CLASS =
  'flex items-center gap-1 rounded-md border border-gray-700/60 bg-container-500/90 px-2 py-1 ' +
  'text-xs text-gray-300 transition hover:bg-container-400 hover:text-white';

/**
 * Control that drops every pinned tooltip on a chart. Renders nothing while
 * there is nothing pinned.
 * @param props - See {@link ClearPinsButtonProps}.
 * @param props.count - How many tooltips are pinned.
 * @param props.onClear - Removes them all.
 * @param props.className - Overrides the default pill styling.
 * @returns The button, or null.
 */
export function ClearPinsButton({
  count,
  onClear,
  className
}: ClearPinsButtonProps): ReactElement | null {
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onClear}
      title="Remove all pinned tooltips"
      className={className || CLEAR_PINS_CLASS}
    >
      <X className="h-3 w-3" />
      Clear pins ({count})
    </button>
  );
}
